import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";

import { Action, Category } from "constants/events";
import { Coordinates } from "custom-types/Coordinates";
import { VariantWithDeliveryInfo } from "custom-types/Variant";
import useFilters from "hooks/useFilters";
import useQueryParamState from "hooks/useQueryParamState";
import { getIsValidStreetAddress } from "redux/selectors/location";

import { mapFilterObjectToAPIV2 } from "components/DeliveryGateway/utils/mapUIFiltersToAPI";
import { Facet } from "components/FilteredGrid";
import MenuFilters from "components/MenuFilters";
import useChips from "components/MenuFilters/hooks/useChips";
import useMenuFilterSearch from "components/MenuFilters/hooks/useMenuFilterSearch";
import { MenuPage } from "components/MenuFilters/types/MenuFilters";

import AddressCaptureModal from "../AddressCaptureModal";
import DeliveryProductResults from "../DeliveryProductResults";
import { SortState } from "../ShopCoreSort/ShopCoreSort";

export const PICKUP_FILTERS = {
  max_distance: "20",
  pickup_available: "true",
};

export const DEFAULT_FILTERS = {
  dispensary_retail_type: "dispensary",
};

// * Must be a constant to prevent rerenders.
const IGNORED_QUERIES = [
  "dispensarySort",
  "fulfillmentPreference",
  "attemptedRetailerName",
];

const DeliveryMenu = ({
  coordinates: { lat, lon },
  enablePickupMode,
  isDeliveryAvailable,
  renderPreFilteredView,
}: {
  coordinates: Coordinates;
  enablePickupMode?: boolean;
  isDeliveryAvailable?: boolean;
  renderPreFilteredView: () => React.ReactNode;
}) => {
  const [q, _setQ] = useQueryParamState("q");

  const { filterValues, filtersApplied, clearAllFilters } = useFilters({
    ignoredQueries: IGNORED_QUERIES,
  });
  const {
    searchShopCoreMenu,
    results: { availableFilters, totalItems, menuItems },
    loading,
  } = useMenuFilterSearch();
  const { handleFilterChipClick } = useChips();
  const [menuItemSort, setMenuItemSort] = useState<SortState | null>(null);
  const isValidAddress = useSelector(getIsValidStreetAddress);
  const [modalVisible, setModalVisible] = useState(false);
  const modalReturnRef = createRef<HTMLButtonElement>();

  const publishFilterPanelImpression = {
    action: Action.impression,
    category: Category.deliveryGateFlow,
    label: "filter panel",
    nonInteractionEvent: true,
  };

  const hasFiltersButNoAddress = filtersApplied && !isValidAddress;
  const searchParams: Parameters<typeof searchShopCoreMenu>[2] = useMemo(
    () => ({
      filters: mapFilterObjectToAPIV2({
        ...filterValues,
        // * Do a pickup search when delivery unavailable
        ...(enablePickupMode ? PICKUP_FILTERS : {}),
        ...DEFAULT_FILTERS,
      }),
      ...(q ? { q: String(q) } : {}),
      ...(menuItemSort ? menuItemSort : {}),
      // * Only grab metadata if user has no active query.
      ...(!filtersApplied ? { take: 0 } : {}),
    }),
    [filterValues, q, menuItemSort, enablePickupMode]
  );

  useEffect(() => {
    searchShopCoreMenu(lat, lon, searchParams);
  }, [lat, lon, searchParams]);

  const onClickLoadMore = useCallback(
    () =>
      searchShopCoreMenu(
        lat,
        lon,
        {
          ...searchParams,
          skip: menuItems.length,
        },
        true
      ),
    [lat, lon, searchParams, menuItems]
  );

  const onClearAllClick = (facet?: Facet) => {
    if (!facet) {
      setMenuItemSort(null);
      clearAllFilters();
      return hasFiltersButNoAddress ? showAddressCaptureModal : undefined;
    }
    handleFilterChipClick(facet);
    return hasFiltersButNoAddress ? showAddressCaptureModal : undefined;
  };

  const showAddressCaptureModal = () => setModalVisible(true);

  return (
    <div className="mt-xs md:mt-lg">
      {!isDeliveryAvailable && Object.keys(filterValues).length > 0 && (
        <h3 className="md:hidden px-lg pb-xs mt-md">
          Try ordering for in-store pickup.
        </h3>
      )}

      <MenuFilters
        availableFilters={availableFilters}
        onClearAllClick={onClearAllClick}
        mobileFilterPanelImpression={publishFilterPanelImpression}
        loading={loading}
        menuPage={MenuPage.delivery}
      >
        {q || filtersApplied ? (
          <>
            <AddressCaptureModal
              isVisible={modalVisible}
              onDismissed={() => setModalVisible(false)}
              returnFocusRef={modalReturnRef}
            />
            <DeliveryProductResults
              coordinates={{ lat, lon }}
              enablePickupMode={enablePickupMode}
              isDeliveryAvailable={isDeliveryAvailable}
              menuItems={menuItems as VariantWithDeliveryInfo[]}
              loading={loading}
              onClickLoadMore={onClickLoadMore}
              selectedSort={menuItemSort}
              onChangeSort={setMenuItemSort}
              onShowAddressCaptureModal={showAddressCaptureModal}
              totalCount={totalItems}
            />
          </>
        ) : (
          renderPreFilteredView()
        )}
      </MenuFilters>
    </div>
  );
};

export default DeliveryMenu;
